<template>
    <div id="carousel-root" ref="root">
        <div ref="wrapper" style="position: relative; height: 100%; width: 100%;">
            <div
                ref="carousel"
                :class="{
                    'carousel-grid': true,
                    single: onlyShowSingleItem || !hasItems,
                    'should-center': shouldCentre,
                    'tight-cols': tightCols,
                    'extra-tight-cols': extraTightCols,
                    'px-5': alwaysShowArrows,
                }"
            >
                <template v-if="hasItems">
                    <component
                        :is="component"
                        v-for="(item, index) in items"
                        :key="index"
                        class="scroll-snap-to"
                        v-bind="item"
                        @click="() => handleItemClick(item)"
                    />
                </template>

                <div v-else class="py-3 d-flex flex-column justify-content-center">
                    <div v-if="noResourcesImage">
                        <img class="no-resource-image mb-3" :src="noResourcesImage" />
                    </div>
                    <div class="d-flex flex-column justify-content-center align-items-center">
                        <HorizontalPulse v-if="hasPulse" class="mb-3" />
                        <h4>
                            {{ noResource }}
                        </h4>
                    </div>
                </div>
            </div>

            <IconBtn
                v-if="!shouldHideArrows"
                name="arrow-left"
                size="lg"
                :class="`primary-outlined ${alwaysShowArrows ? '' : 'hide-when-not-hovered'}`"
                style="position: absolute; left: 4px; background-color: white;"
                :style="{ top: top + 'px' }"
                @click="scrollLeft"
            />

            <IconBtn
                v-if="!shouldHideArrows"
                name="arrow-right"
                size="lg"
                :class="`primary-outlined ${alwaysShowArrows ? '' : 'hide-when-not-hovered'}`"
                style="position: absolute; right: 4px; background-color: white;"
                :style="{ top: top + 'px' }"
                @click="scrollRight"
            />
        </div>
    </div>
</template>

<script>
import CarouselDefault from './Carousel.Default';
import HorizontalPulse from './HorizontalPulse.vue';

export default {
    name: 'Carousel',
    components: { CarouselDefault, HorizontalPulse },

    data() {
        return {
            top: 100,
            width: '350px',
        };
    },

    props: {
        component: {
            type: Object,
            optional: true,
            default: () => CarouselDefault,
        },
        items: {
            type: Array,
            default: () => [],
        },
        noResourcesImage: {
            type: [Object, String],
            default: '',
        },
        noResourcesMessage: {
            type: String,
            default: '',
        },
        hasPulse: {
            type: Boolean,
            default: false,
        },

        onlyShowSingleItem: Boolean, // Stops the carousel from displaying more than one item at a time
        hideArrows: Boolean, // Removes the previous/next arrows from the carousel
        tightCols: Boolean,
        extraTightCols: Boolean,
        dontCenter: Boolean,
        alwaysShowArrows: Boolean,
    },

    computed: {
        shouldHideArrows() {
            return (
                this.hideArrows ||
                (this.items.length <= 4 && !this.onlyShowSingleItem) ||
                this.items.length <= 1
            );
        },
        shouldCentre() {
            return (
                this.shouldHideArrows &&
                !this.onlyShowSingleItem &&
                this.items.length < 4 &&
                !this.dontCenter
            );
        },
        canScrollLeft() {
            return this.$refs.carousel.scrollLeft !== 0;
        },
        canScrollRight() {
            return this.$refs.carousel.scrollLeft === this.$refs.carousel.scrollWidth;
        },
        hasItems() {
            return this.items && this.items.length > 0;
        },
        noResource() {
            return this.noResourcesMessage || this.$t('general.noResourcesFound');
        },
    },

    methods: {
        scrollLeft() {
            // The constant that is added on, should be the same value as the --grid-gap variable set in the css
            const widthToScroll = this.$refs.carousel.clientWidth + 10;
            this.$refs.carousel.scrollLeft -= widthToScroll;
        },
        scrollRight() {
            // The constant that is added on, should be the same value as the --grid-gap variable set in the css
            const widthToScroll = this.$refs.carousel.clientWidth + 10;
            this.$refs.carousel.scrollLeft += widthToScroll;
        },
        handleResizeEvent() {
            this.top = this.$refs.wrapper.clientHeight / 2 - 15;
            // const carouselRoot = document.getElementById('carousel-root');
            // if (carouselRoot) {
            //     const parentWidth = carouselRoot.parentElement.clientWidth;
            //     this.width = parentWidth + 'px';
            // }
        },
        handleItemClick(item) {
            this.$emit('item-click', item);
        },
    },

    mounted() {
        this.$nextTick(() => {
            this.handleResizeEvent();
            window.addEventListener('resize', this.handleResizeEvent);
        });
        this.handleResizeEvent();
    },

    destroyed() {
        window.removeEventListener('resize', this.handleResizeEvent);
    },
};
</script>

<style lang="scss" scoped>
#carousel-root {
    .carousel-grid {
        --grid-gap: 10px;

        display: grid;
        grid-auto-columns: calc(25% - 3 / 4 * var(--grid-gap));
        grid-auto-flow: column;

        width: 100%;
        min-height: 160px;
        overflow-x: auto;
        overflow-y: hidden;

        scrollbar-width: none;
        scroll-behavior: smooth;
        word-break: break-word;
        text-align: center;

        &.tight-cols {
            grid-auto-columns: calc(12% - 3 / 4 * var(--grid-gap));
        }
        &.extra-tight-cols {
            gap: 10px;
            grid-auto-columns: 55px;
        }
        &.should-center {
            justify-content: center;
            grid-auto-columns: 0.5fr;
        }
        &.single {
            grid-auto-columns: 100%;
            scroll-snap-type: x proximity;
            scroll-snap-type: proximity;
            scroll-snap-destination: 0 0;
            scroll-snap-points-x: repeat(calc(100% + var(--grid-gap)));
        }

        .scroll-snap-to {
            scroll-snap-align: end;
            scroll-snap-stop: normal;
        }

        .no-resource-image {
            width: 100%;
            max-width: 140px;
            max-height: 100px;

            object-fit: scale-down;
            color: var(--primary);
        }
    }

    .hide-when-not-hovered {
        opacity: 0;
        transition: opacity 0.5s ease-in-out;
    }
    &:hover .hide-when-not-hovered {
        opacity: 1;
    }

    @media only screen and (max-width: 500px) {
        .hide-when-not-hovered {
            opacity: 1;
        }
        .carousel-grid {
            grid-auto-columns: 100%;
            scroll-snap-type: x proximity;
            scroll-snap-type: proximity;
            scroll-snap-destination: 0 0;
            /* scroll-snap-points-x: repeat(100%); */
            scroll-snap-points-x: repeat(calc(100% + var(--grid-gap)));
        }
        .carousel-grid-should-center {
            justify-content: start;
        }
    }
}
</style>
